import { OrderChannel, ReportScheduleScope, ReportType, ReportTypeHelper, Timezone, Weekday, } from '@biteinc/enums';
export const reportScheduleSchema = {
    displayName: 'report schedule',
    type: 'object',
    fields: {
        type: {
            type: 'stringEnum',
            enum: ReportType,
            enumNameHelper: ReportTypeHelper.name,
            required: true,
            ui: 'dropdown',
        },
        scope: {
            type: 'stringEnum',
            enum: ReportScheduleScope,
            ui: 'dropdown',
            required: true,
        },
        currentDay: {
            type: 'bool',
            displayName: 'Current Day',
            tooltip: {
                text: 'If checked, the report will use data from the current day. If unchecked, the report will use data from the previous day.',
            },
            required: true,
            defaultValue: false,
            conditions: [
                {
                    field: 'scope',
                    values: [ReportScheduleScope.Daily],
                },
                {
                    field: 'type',
                    values: [ReportType.FutureOrders],
                },
            ],
        },
        day: {
            type: 'stringEnum',
            enum: Weekday,
            ui: 'dropdown',
            required: true,
            conditions: [
                {
                    field: 'scope',
                    values: [ReportScheduleScope.Weekly],
                },
            ],
        },
        dayOfMonth: {
            type: 'int',
            displayName: 'Day of Month',
            min: 1,
            max: 31,
            required: true,
            ui: 'dropdown',
            conditions: [
                {
                    field: 'scope',
                    values: [ReportScheduleScope.Monthly],
                },
            ],
            tooltip: {
                text: 'An integer from 1 to 31. If the day of the month is greater than the number of days in the month, then the report will run on the last day of the month.',
            },
        },
        time: {
            type: 'dayTimestamp',
            required: true,
        },
        timezone: {
            type: 'stringEnum',
            enum: Timezone,
            ui: 'dropdown',
            tooltip: {
                text: 'All dates in your report will appear in this timezone.',
            },
            required: true,
            conditions: [
                {
                    field: 'type',
                    values: [
                        ReportType.CouponRedemptionHistory,
                        ReportType.CustomerOrders,
                        ReportType.DayUpsell,
                        ReportType.EightySixingActivity,
                        ReportType.FlashPayouts,
                        ReportType.FutureOrders,
                        ReportType.ItemUpsell,
                        ReportType.KioskUsageOverlap,
                        ReportType.KOR,
                        ReportType.LoyaltyLoginAttempts,
                        ReportType.LoyaltySignups,
                        ReportType.LoyaltyUsage,
                        ReportType.OrderConversionRate,
                        ReportType.OrderInsertionRate,
                        ReportType.PilotEngagement,
                        ReportType.PilotLaborAnalysis,
                        ReportType.SalesGuests,
                        ReportType.SalesOrders,
                        ReportType.SalesSummaryOrg,
                        ReportType.SalesTransactions,
                        ReportType.ThirdPartyOrders,
                        ReportType.VitrineSessionMetrics,
                    ],
                },
            ],
        },
        locationGroupId: {
            type: 'mongoId',
            displayName: 'Locations',
            ui: 'dropdown',
            required: true,
            conditions: [
                {
                    field: 'type',
                    values: [
                        ReportType.CouponRedemptionHistory,
                        ReportType.DayUpsell,
                        ReportType.EightySixingActivity,
                        ReportType.FlashPayouts,
                        ReportType.FutureOrders,
                        ReportType.ItemUpsell,
                        ReportType.LoyaltyLoginAttempts,
                        ReportType.LoyaltySignups,
                        ReportType.LoyaltyUsage,
                        ReportType.OrderConversionRate,
                        ReportType.OrderInsertionRate,
                        ReportType.PilotEngagement,
                        ReportType.PilotLaborAnalysis,
                        ReportType.SalesGuests,
                        ReportType.SalesOrders,
                        ReportType.SalesSummaryOrg,
                        ReportType.SalesTransactions,
                        ReportType.ThirdPartyOrders,
                        ReportType.VitrineSessionMetrics,
                    ],
                },
            ],
        },
        salesByLocationGroupIds: {
            type: 'array',
            elementType: 'mongoId',
            displayName: 'Locations Groups to Include in "Sales by Location Group" Section',
            ui: 'table',
            placeholder: 'Select Location Groups',
            conditions: [
                {
                    field: 'type',
                    values: [ReportType.SalesSummaryOrg],
                },
            ],
        },
        emailAddresses: {
            type: 'array',
            elementType: 'email',
            placeholder: 'add a new email address',
            displayName: 'Email Addresses',
            required: true,
            minCount: 1,
        },
        isEnabled: {
            type: 'bool',
            displayName: 'Enabled',
            required: true,
        },
        orderChannels: {
            displayName: 'Order Channels',
            type: 'array',
            elementType: 'stringEnum',
            elementEnum: OrderChannel,
            minCount: 1,
            ui: 'checklist',
            conditions: [
                {
                    field: 'type',
                    values: [
                        ReportType.CouponRedemptionHistory,
                        ReportType.DayUpsell,
                        ReportType.EightySixingActivity,
                        ReportType.FutureOrders,
                        ReportType.ItemUpsell,
                        ReportType.LoyaltyLoginAttempts,
                        ReportType.LoyaltySignups,
                        ReportType.LoyaltyUsage,
                        ReportType.OrderConversionRate,
                        ReportType.OrderInsertionRate,
                        ReportType.PilotEngagement,
                        ReportType.PilotLaborAnalysis,
                        ReportType.SalesGuests,
                        ReportType.SalesOrders,
                        ReportType.SalesSummaryOrg,
                        ReportType.SalesSummarySite,
                        ReportType.SalesTransactions,
                        ReportType.ThirdPartyOrders,
                        ReportType.VitrineSessionMetrics,
                    ],
                },
            ],
            required: true,
        },
        isBiteOnly: {
            type: 'bool',
            displayName: 'Is Bite-Only',
            biteOnly: true,
            tooltip: {
                text: 'Generated report will include Bite-Only data',
            },
            conditions: [
                {
                    field: 'type',
                    values: [
                        ReportType.SalesSummaryChannel,
                        ReportType.SalesSummarySite,
                        ReportType.SalesSummaryOrg,
                    ],
                },
            ],
        },
        vendorId: {
            type: 'mongoId',
            displayName: 'Vendor',
            ui: 'dropdown',
            required: true,
            conditions: [
                {
                    field: 'type',
                    values: [ReportType.VendorPayouts],
                },
            ],
        },
    },
};
//# sourceMappingURL=report_schedule_schema.js.map